import { Inset } from '@fox/layout/src/flex';
import { LocalizationProvider, StaticDateRangePicker, StaticDateRangePickerProps } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import MUIDateRangePicker, { DateRangePickerProps } from '@mui/lab/DateRangePicker';
import { RangeInput } from '@mui/lab/internal/pickers/date-utils';
import { TextField, TextFieldProps } from '@mui/material';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { DISABLE_BROWSER_AUTOFILL_SETTINGS } from '../../utils/disable-auto-complete';

export const ClearIconWrapper = styled.div<{ $visible: boolean }>`
	cursor: ${props => (props.$visible ? 'pointer' : 'inherit')};
	opacity: 0;
	transition: opacity 0.3s ease-in-out;
	position: absolute;
	right: 5px;
	transform: scale(0.75);
`;
export const DateClearIconWrapper = styled.div<{ $visible: boolean }>`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: transparant;
	position: relative;
	&:hover ${ClearIconWrapper} {
		opacity: ${props => (props.$visible ? 1 : 0)};
	}
`;

interface IDateRangePickerProps
	extends Pick<DateRangePickerProps<Date>, 'readOnly' | 'startText' | 'endText' | 'onChange' | 'maxDate' | 'minDate'>,
		Pick<TextFieldProps, 'size'> {
	hideClearIcon?: boolean;
	hideBorder?: boolean;
	value?: Date[];
}

/**
 * DateRangePicker is deprecated, use `{ DateRangeSelect } from '@fox/ui'`. Please see Storybook for help with DateRangeSelect and report any issues.
 * @deprecated
 */
export const DateRangePicker = React.forwardRef<HTMLDivElement, IDateRangePickerProps>((props, ref) => {
	const inputValue: RangeInput<Date> = props.value
		? [moment(props.value[0]).isValid() ? props.value[0] : null, moment(props.value[1]).isValid() ? props.value[1] : null]
		: [null, null];
	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<MUIDateRangePicker<Date>
				ref={ref}
				startText={props.startText}
				endText={props.endText}
				value={inputValue}
				maxDate={props.maxDate}
				minDate={props.minDate}
				onChange={newValue => {
					if (props.onChange) {
						props.onChange(newValue);
					}
				}}
				inputFormat={'dd/MM/yyyy'}
				renderInput={(startProps, endProps) => (
					<div style={{ gap: 5, display: 'flex' }}>
						<TextField
							{...DISABLE_BROWSER_AUTOFILL_SETTINGS}
							{...startProps}
							style={{ boxSizing: 'content-box' }}
							variant={props.hideBorder ? 'standard' : undefined}
							InputProps={{
								disableUnderline: props.hideBorder
							}}
							size={props.size ?? 'small'}
						/>
						<TextField
							{...DISABLE_BROWSER_AUTOFILL_SETTINGS}
							{...endProps}
							style={{ boxSizing: 'content-box' }}
							variant={props.hideBorder ? 'standard' : undefined}
							InputProps={{
								disableUnderline: props.hideBorder
							}}
							size={props.size ?? 'small'}
						/>
					</div>
				)}
			/>
		</LocalizationProvider>
	);
});

type IStaticDateRangePickerProps = Omit<StaticDateRangePickerProps, 'renderInput'>;

export const StaticDateRangeSelector = React.forwardRef((props: IStaticDateRangePickerProps, ref) => {
	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<StaticDateRangePicker
				inputFormat="dd-MM-yyyy"
				mask={'__-__-____'}
				renderInput={(startProps, endProps) => {
					return (
						<>
							<TextField {...DISABLE_BROWSER_AUTOFILL_SETTINGS} {...startProps} />
							<Inset> to </Inset>
							<TextField {...DISABLE_BROWSER_AUTOFILL_SETTINGS} {...endProps} />
						</>
					);
				}}
				{...props}
			/>
		</LocalizationProvider>
	);
});
